import { Controller } from '@hotwired/stimulus'
import SFetch from '@app/utils/sfetch'
import Polling from '@app/utils/polling'
import { SfLog } from '@app/adapters/logger'

export default class extends Controller {
  static targets = ['button']
  static values = {
    actionUrl: String,
    deactivateButton: String,
    successMsg: String,
  }

  declare readonly buttonTarget: HTMLElement
  declare readonly actionUrlValue: string
  declare readonly deactivateButtonValue: string
  declare readonly successMsgValue: string
  private polling?: Polling<() => void>

  connect(): void {
    this.buttonTarget.addEventListener('click', this.handleButtonClick)
  }

  disconnect(): void {
    this.polling?.unsubscribe()
    this.buttonTarget.removeEventListener('click', this.handleButtonClick)
  }

  handleButtonClick = (event: Event): void => {
    const button = event.currentTarget as HTMLElement
    event.preventDefault()
    event.stopPropagation()
    // pointer-events: none on the busy button stops mouse clicks, not Enter.
    if (button.getAttribute('aria-busy') === 'true') return

    this.setButtonWaitingState(button)
    void (async (): Promise<void> => {
      try {
        const response = await this.activatePackage()

        if (!response.success) {
          this.removeButtonWaitingState(button)
          if (response.message) {
            window.SilverfinFlash.addFlash('error', response.message.toString())
          } else {
            SfLog.error('Activation failed for ' + this.actionUrlValue)
          }
        } else {
          if (
            response.messageBusChannelName &&
            response.messageBusLastId !== undefined
          ) {
            this.polling = new Polling(
              response.messageBusChannelName,
              response.messageBusLastId,
              () => this.packageActivated()
            )
            this.polling.subscribe()
          }
        }
      } catch (error) {
        this.removeButtonWaitingState(button)
        const message = (error as { body?: { message?: unknown } } | null)?.body
          ?.message
        if (typeof message === 'string') {
          window.SilverfinFlash.addFlash('error', message, 3000)
        } else {
          SfLog.error('Activation failed for ' + this.actionUrlValue)
        }
      }
    })()
  }

  private setButtonWaitingState(btn: HTMLButtonElement | HTMLElement): void {
    btn.setAttribute('aria-disabled', 'true')
    btn.setAttribute('aria-busy', 'true')
  }

  private removeButtonWaitingState(btn: HTMLButtonElement | HTMLElement): void {
    btn.removeAttribute('aria-disabled')
    btn.removeAttribute('aria-busy')
  }

  private async activatePackage(): Promise<{
    success: boolean
    message?: string
    messageBusChannelName?: string
    messageBusLastId?: number
  }> {
    const response = await SFetch.raw(this.actionUrlValue, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    })
    const data = (await response.json()) as {
      success: boolean
      message?: string
      messageBusChannelName?: string
      messageBusLastId?: number
    }
    return data
  }

  private packageActivated(): void {
    window.requestAnimationFrame(() => {
      window.SilverfinFlash.addFlash(
        'success',
        this.successMsgValue.toString(),
        3000
      )
      if (this.deactivateButtonValue !== '') {
        const temp = document.createElement('span')
        temp.innerHTML = this.deactivateButtonValue
        const btn = temp.firstElementChild
        if (btn) {
          this.buttonTarget.replaceWith(btn)
        } else {
          this.buttonTarget.style.display = 'none'
        }
      } else {
        this.buttonTarget.style.display = 'none'
      }
    })

    this.polling?.unsubscribe()
  }
}
